import React, { useEffect, useMemo, useState } from "react";
import confetti from "canvas-confetti";
import { Sparkles, Rocket, X } from "lucide-react";

const PLAN_THEME = {
  starter: {
    color: "#378ADD",
    label: "Starter",
    line: "One small step for you — a giant leap in your eating journey.",
    sub: "Saturday lists, budget alerts and unlimited uploads are now yours.",
  },
  pro: {
    color: "#FF6B00",
    label: "Pro",
    line: "Welcome to the 360° food intelligence club.",
    sub: "Every app, every order, every habit — finally in one dashboard.",
  },
  family: {
    color: "#1D9E75",
    label: "Family",
    line: "The whole household, on one smarter food plan.",
    sub: "Add up to four members and watch your family savings compound.",
  },
};

const SUCCESS_LINES = [
  "You're now spending smarter than 87% of urban Indian households.",
  "Your food bill just met its match.",
  "Welcome to the household-savings hall of fame.",
  "Brace your wallet — the savings start now.",
];

/**
 * SubscriptionCelebrationModal — E2: shown after a successful Razorpay
 * verify(). Pops confetti, displays the new plan badge, a motivational
 * one-liner with a touch of humour, and auto-dismisses after 6s. Users can
 * close manually anytime.
 */
export default function SubscriptionCelebrationModal({ open, plan, isUpgrade, fromPlan, onClose }) {
  const theme = PLAN_THEME[plan] || PLAN_THEME.pro;
  const fromTheme = fromPlan ? PLAN_THEME[fromPlan] : null;

  const surprise = useMemo(
    () => SUCCESS_LINES[Math.floor(Math.random() * SUCCESS_LINES.length)],
    [open]
  );

  const [closing, setClosing] = useState(false);

  // Confetti burst when modal opens
  useEffect(() => {
    if (!open) return;
    const colors = [theme.color, "#FFFFFF", "#1D9E75", "#FFCB11"];
    const runBurst = (origin) => {
      try {
        confetti({
          particleCount: 80,
          spread: 70,
          startVelocity: 45,
          origin,
          colors,
          ticks: 220,
          scalar: 1.05,
          zIndex: 200,
        });
      } catch {
        /* canvas not ready, no-op */
      }
    };
    runBurst({ x: 0.2, y: 0.6 });
    setTimeout(() => runBurst({ x: 0.8, y: 0.6 }), 180);
    setTimeout(() => runBurst({ x: 0.5, y: 0.3 }), 360);
    // gentle trailing rain
    const t = setTimeout(() => {
      try {
        confetti({
          particleCount: 40,
          spread: 100,
          startVelocity: 25,
          origin: { x: 0.5, y: 0 },
          colors,
          gravity: 0.6,
          ticks: 200,
          zIndex: 200,
        });
      } catch {
        /* no-op */
      }
    }, 700);
    return () => clearTimeout(t);
  }, [open, theme.color]);

  // Auto-dismiss after 6s
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setClosing(true);
      setTimeout(onClose, 280);
    }, 6000);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="celebration-title"
      data-testid="subscription-celebration-modal"
      style={{
        animation: closing ? "celebFadeOut 0.28s ease-in forwards" : "celebFadeIn 0.32s ease-out forwards",
      }}
    >
      <style>{`
        @keyframes celebFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes celebFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes celebSlideIn {
          from { transform: translateY(40px) scale(0.9); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes celebPulseRing {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.0); }
          50% { box-shadow: 0 0 0 18px rgba(255,255,255,0.04); }
        }
      `}</style>

      <button
        type="button"
        onClick={() => {
          setClosing(true);
          setTimeout(onClose, 280);
        }}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-[#888] hover:text-[#F0EDE8] z-10"
        aria-label="Close"
        data-testid="celebration-close"
      >
        <X className="w-5 h-5" />
      </button>

      <div
        className="relative w-full max-w-lg text-center px-6 py-10 md:px-12 md:py-14"
        style={{
          animation: "celebSlideIn 0.45s cubic-bezier(0.2, 0.7, 0.2, 1) forwards",
          background:
            "radial-gradient(ellipse at center, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0) 70%)",
        }}
      >
        {/* Plan badge */}
        <div
          className="mx-auto w-24 h-24 md:w-28 md:h-28 flex items-center justify-center mb-6 relative"
          style={{
            background: theme.color,
            color: "#fff",
            animation: "celebPulseRing 2.4s ease-in-out infinite",
          }}
        >
          <Rocket className="w-10 h-10 md:w-12 md:h-12" />
          <span
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 text-[9px] uppercase tracking-[0.22em] font-bold"
            style={{ background: "#fff", color: theme.color }}
          >
            {theme.label}
          </span>
        </div>

        {/* Eyebrow */}
        <div
          className="text-[11px] uppercase tracking-[0.22em] font-bold mb-3 flex items-center justify-center gap-2"
          style={{ color: theme.color }}
        >
          <Sparkles className="w-3.5 h-3.5" />
          {isUpgrade && fromTheme
            ? `${fromTheme.label.toUpperCase()} → ${theme.label.toUpperCase()}`
            : `Welcome to ${theme.label.toUpperCase()}`}
          <Sparkles className="w-3.5 h-3.5" />
        </div>

        {/* Headline */}
        <h2
          id="celebration-title"
          className="font-display uppercase text-3xl md:text-4xl leading-[1.05] mb-4 text-[#F0EDE8]"
          data-testid="celebration-headline"
        >
          {theme.line}
        </h2>

        {/* Italic supporting line */}
        <p
          className="text-base md:text-lg text-[#F0EDE8]/90 leading-relaxed mb-3"
          style={{ fontFamily: "'Georgia', serif", fontStyle: "italic" }}
          data-testid="celebration-sub"
        >
          {theme.sub}
        </p>

        {/* Humorous flourish */}
        <p className="text-sm text-[#888] mb-7 leading-relaxed" data-testid="celebration-flourish">
          {surprise}
        </p>

        <button
          type="button"
          onClick={() => {
            setClosing(true);
            setTimeout(onClose, 280);
          }}
          className="font-bold uppercase tracking-wider py-3 px-8 text-sm transition-transform hover:scale-105"
          style={{ background: theme.color, color: "#fff" }}
          data-testid="celebration-cta"
        >
          Take me to my dashboard
        </button>
      </div>
    </div>
  );
}
